<template>
    <div>
        <base-form
            ref="form"
            v-model="form"
            :api="api"
            :mapper="mapper"
            :entity-id="entityId"
            :title="title"
            :disabled="disabled"
            @created="created"
            @updated="updated"
            @cancelled="cancelled"
            @deleted="deleted"
        >
            <form-panel>
                <form-two-col-row>
                    <template v-slot:col1>
                        <text-input
                            v-model="form.name"
                            v-validate.immediate="'required|max:255|space'"
                            name="name"
                            label="labels.name"
                            :error-messages="errors.collect('name')"
                            :data-vv-as="$t('labels.name')"
                        ></text-input>
                    </template>
                    <template v-slot:col2>
                        <date-picker-input
                            v-model="form.cutoffTime"
                            v-validate.immediate="'required|dateafter'"
                            :label="$t('labels.cc_cutoff.date')"
                            name="cutoffTimeDate"
                            :error-messages="errors.collect('cutoffTimeDate')"
                            :data-vv-as="$t('labels.cc_cutoff.date')"
                            :min="minDate"
                        ></date-picker-input>
                    </template>
                </form-two-col-row>
                <form-two-col-row>
                    <template v-slot:col1>
                        <checkbox-input
                            v-model="form.canGroupOrder"
                            name="canGroupOrder"
                            label="labels.can-group-order"
                        ></checkbox-input>
                    </template>
                    <template v-slot:col2>
                        <div class="d-flex">
                            <date-picker-input
                                v-model="form.deliveryTime"
                                v-validate.immediate="'required|dateafter'"
                                :label="$t('labels.cc_delivery_at.date')"
                                name="deliveryTimeDate"
                                class="w-100"
                                :error-messages="
                                    errors.collect('deliveryTimeDate')
                                "
                                :data-vv-as="$t('labels.cc_delivery_at.date')"
                                :min="minDate"
                            ></date-picker-input>
                            <!-- text field - for design purposes -->
                            <v-text-field
                                :value="`${$options.filters.toDate(
                                    form.deliveryTime,
                                    'HH:mm',
                                    30
                                )}`"
                                prepend-inner-icon="remove"
                                class="pl-2"
                                readonly
                            ></v-text-field>
                        </div>
                    </template>
                </form-two-col-row>
                <form-two-col-row>
                    <template v-slot:col1>
                        <entity-select-input
                            v-model="form.relatedRestaurants"
                            v-validate.immediate="'required'"
                            label="labels.restaurants"
                            :data-vv-as="$t('labels.restaurants')"
                            :entity="SelectApiClientType.RESTAURANT"
                            :query-params="{ ccCompanyPartner: form.company }"
                            multi-select
                            name="restaurants"
                            :error-messages="errors.collect('restaurants')"
                        ></entity-select-input>
                    </template>
                    <template v-slot:col2>
                        <entity-select-input
                            v-model="form.addresses"
                            v-validate.immediate="'required'"
                            label="labels.addresses"
                            type="checkbox"
                            :data-vv-as="$t('labels.addresses')"
                            :entity="SelectApiClientType.COMPANY_ADDRESS"
                            :query-params="{ company: form.company }"
                            multi-select
                            name="addresses"
                            :error-messages="errors.collect('addresses')"
                        ></entity-select-input>
                    </template>
                </form-two-col-row>
                <form-two-col-row>
                    <template v-slot:col1>
                        <currency-input
                            v-model="form.budgetLimit"
                            v-validate.immediate="
                                'required|min_value:0|decimal:2'
                            "
                            name="budgetLimit"
                            label="labels.budget"
                            :error-messages="errors.collect('budgetLimit')"
                            :data-vv-as="$t('labels.budget')"
                        ></currency-input>

                        <h3 class="headline mt-4">
                            {{ $t('labels.cc_budget_overrides') }}
                        </h3>
                        <v-row>
                            <v-spacer></v-spacer>
                            <v-col class="text-right">
                                <add-budget-override
                                    :base-limit="form.budgetLimit"
                                    @added="addBudgetOverride"
                                ></add-budget-override>
                            </v-col>
                        </v-row>

                        <v-row
                            v-for="(override, index) in form.budgetOverrides"
                            :key="override['@id']"
                            class="pt-2"
                        >
                            <v-col cols="6" class="py-0">
                                <entity-select-input
                                    v-model="override.user"
                                    v-validate.immediate="'required'"
                                    :name="`overrideUser[${override['@id']}]`"
                                    label="labels.employee"
                                    :entity="selectApiClientType.USER"
                                    :query-params="{
                                        company: companyApiClient.getItemIri(
                                            companyId
                                        ),
                                    }"
                                    :items-filter="
                                        overrideEmployeesFilter(override)
                                    "
                                    :needs-update="overrideSelectUpdate"
                                    autocomplete
                                    :data-vv-as="$t('labels.employee')"
                                    :error-messages="
                                        errors.collect(
                                            `overrideUser[${override['@id']}]`
                                        )
                                    "
                                    @input="
                                        overrideSelectUpdate = !overrideSelectUpdate
                                    "
                                ></entity-select-input>
                            </v-col>

                            <v-col cols="4" class="py-0">
                                <currency-input
                                    v-model="override.budgetLimit"
                                    v-validate.immediate="'required|decimal:2'"
                                    :name="`overrideBudgetLimit[${override['@id']}]`"
                                    label="labels.budget"
                                    type="number"
                                    :data-vv-as="$t('labels.budget')"
                                    :error-messages="
                                        errors.collect(
                                            `overrideBudgetLimit[${override['@id']}]`
                                        )
                                    "
                                ></currency-input>
                            </v-col>

                            <v-col cols="2" class="py-0 pt-3">
                                <v-btn
                                    icon
                                    color="error"
                                    @click="removeBudgetOverride(index)"
                                >
                                    <v-icon>delete</v-icon>
                                </v-btn>
                            </v-col>
                        </v-row>

                        <div class="pb-7"></div>
                    </template>
                    <template v-slot:col2>
                        <v-row>
                            <v-col cols="6">
                                <text-input
                                    v-model="form.pickupOffset"
                                    v-validate="'required|numeric'"
                                    type="number"
                                    name="pickupOffset"
                                    label="labels.pickup-offset"
                                    :error-messages="
                                        errors.collect('pickupOffset')
                                    "
                                    :data-vv-as="$t('labels.pickup-offset')"
                                ></text-input>
                            </v-col>
                            <v-col cols="6">
                                <text-input
                                    v-model="form.sendNotificationBeforeCutoff"
                                    v-validate="'required|numeric'"
                                    type="number"
                                    name="sendNotificationBeforeCutoff"
                                    label="labels.send_notification_before_cutoff"
                                    :error-messages="
                                        errors.collect(
                                            'send_notification_before_cutoff'
                                        )
                                    "
                                    :data-vv-as="
                                        $t(
                                            'labels.send_notification_before_cutoff'
                                        )
                                    "
                                ></text-input>
                            </v-col>
                        </v-row>
                        <checkbox-input
                            v-model="form.isRecurring"
                            name="isRecurring"
                            label="labels.is-recurring"
                        ></checkbox-input>
                        <v-row>
                            <v-col cols="6">
                                <text-input
                                    v-model="form.recurringOccasionLimit"
                                    v-validate="'required|numeric'"
                                    type="number"
                                    name="recurringOccasionLimit"
                                    label="labels.recurring-occasion-limit"
                                    :error-messages="
                                        errors.collect('recurringOccasionLimit')
                                    "
                                    :data-vv-as="
                                        $t('labels.recurring-occasion-limit')
                                    "
                                ></text-input>
                            </v-col>
                            <v-col cols="6">
                                <text-input
                                    v-model="form.recurringDayLimit"
                                    v-validate="'required|numeric'"
                                    type="number"
                                    name="recurringDayLimit"
                                    label="labels.recurring-day-limit"
                                    :error-messages="
                                        errors.collect('recurringDayLimit')
                                    "
                                    :data-vv-as="
                                        $t('labels.recurring-day-limit')
                                    "
                                ></text-input>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="6">
                                <select-input
                                    v-model="form.expenseCodeRequired"
                                    name="expenseCodeRequired"
                                    :items="expenseCodeRequired"
                                    label="labels.expense_code_settings"
                                ></select-input>
                            </v-col>
                            <v-col cols="6">
                                <checkbox-input
                                    v-model="form.expenseCodeOnlyFromList"
                                    label="texts.employee_can_only_input_expense_code_from_list"
                                    name="timeSettingsDontApplyToNonCompanyAddresses"
                                ></checkbox-input>
                            </v-col>
                        </v-row>
                        <tag-input
                            v-model="form.expenseCodeList"
                            label="labels.expense_code_list"
                        ></tag-input>
                        <p class="font-italic">
                            {{ $t('labels.combobox_hint') }}
                        </p>
                    </template>
                </form-two-col-row>
            </form-panel>
        </base-form>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import routeType from '@/router/routeType'
import ApiClientType from '@/api/RestApiType'
import configType from '@/store/type/configType'
import MapperType from '@/services/mapper/MapperType'
import SelectApiClientType from '@/api/SelectApiClientType'
import RestApiCollection from '@/api/RestApiCollection'
import FormBase from '@/components/mixins/FormBase'
import FormPanel from '@/components/form/FormPanel'
import FormTwoColRow from '@/components/form/FormTwoColRow'
import TextInput from '@/components/form/Inputs/TextInput'
import CurrencyInput from '@/components/form/Inputs/CurrencyInput'
import DatePickerInput from '@/components/form/Inputs/DatePickerInput'
import EntitySelectInput from '@/components/form/Inputs/EntitySelectInput'
import AddBudgetOverride from './AddBudgetOverride'
import CheckboxInput from '@/components/form/Inputs/CheckboxInput'
import SelectInput from '@/components/form/Inputs/SelectInput'
import TagInput from '@/components/form/Inputs/TagInput'

export default {
    components: {
        FormPanel,
        FormTwoColRow,
        TextInput,
        CurrencyInput,
        DatePickerInput,
        EntitySelectInput,
        CheckboxInput,
        AddBudgetOverride,
        SelectInput,
        TagInput,
    },
    mixins: [FormBase],
    props: {
        companyId: {
            type: [String, Number],
            required: true,
        },
    },
    data() {
        return {
            api: ApiClientType.COMPANY_CLOUDCANTEENS,
            mapper: MapperType.COMPANY_CLOUDCANTEENS_FORM,
            form: {
                name: null,
                cutoffTime: null,
                deliveryTime: null,
                budgetLimit: null,
                relatedRestaurants: [],
                addresses: [],
                pickupOffset: 15,
                canGroupOrder: false,
                isRecurring: false,
                recurringOccasionLimit: 0,
                recurringDayLimit: 0,
                budgetOverrides: [],
                sendNotificationBeforeCutoff: null,
            },
            entity: 'labels.company_cloudcanteens',
            editRoute: routeType.COMPANY_CLOUDCANTEEN_EDIT,
            listRoute: routeType.COMPANY_CLOUDCANTEEN_LIST,
            companyApiClient: RestApiCollection.get(ApiClientType.COMPANIES),
            SelectApiClientType,
            overrideSelectUpdate: false,
        }
    },
    computed: {
        ...mapGetters({
            currencyCode: configType.getters.CURRENCY_CODE,
            currencyLocale: configType.getters.CURRENCY_LOCALE,
        }),
        minDate() {
            return this.$date().format('YYYY-MM-DD')
        },
        expenseCodeRequired() {
            return [
                {
                    title: this.$t('labels.not_required'),
                    value: null,
                },
                {
                    title: this.$t('labels.optional'),
                    value: 1,
                },
                {
                    title: this.$t('labels.required'),
                    value: 2,
                },
            ]
        },
    },
    created() {
        this.form.company = this.companyApiClient.getItemIri(this.companyId)
        this.loadCompanyData()
    },
    methods: {
        editRedirectParams(data) {
            return {
                cloudcanteenId: data.id,
                companyId: this.companyId,
            }
        },
        listRedirectParams() {
            return { companyId: this.companyId }
        },
        removeBudgetOverride(index) {
            this.form.budgetOverrides.splice(index, 1)
        },
        addBudgetOverride(val) {
            if (
                this.form.budgetOverrides &&
                Array.isArray(this.form.budgetOverrides)
            ) {
                this.$set(this.form, 'budgetOverrides', [
                    ...this.form.budgetOverrides,
                    val,
                ])
            } else {
                this.$set(this.form, 'budgetOverrides', [val])
            }
        },
        overrideEmployeesFilter(item) {
            return (values) =>
                values.filter(
                    (value) =>
                        value.key === item.user ||
                        !this.form.budgetOverrides ||
                        this.form.budgetOverrides.length === 0 ||
                        !this.form.budgetOverrides
                            .map((ovr) => ovr.user)
                            .includes(value.key)
                )
        },
        loadCompanyData() {
            this.companyApiClient.get(this.companyId).then((response) => {
                if (this.form.sendNotificationBeforeCutoff === null)
                    this.$set(
                        this.form,
                        'sendNotificationBeforeCutoff',
                        response.defaultCanteenNotificationTime
                    )
            })
        },
    },
}
</script>
